/**
 * PracticeReviewView — the Review of a completed Record & Review session.
 *
 * Presentational: the review document and the note handlers come from the
 * container (PracticeReviewPage), so the rendering is testable without a store.
 *
 * Truthful by construction:
 *   - playback: an UNAVAILABLE recording is "Recording not kept", never a
 *     player that cannot play;
 *   - timeline: segment times are shown only when MEASURED; otherwise the
 *     segments are listed without times and the page says why;
 *   - metrics: an UNAVAILABLE metric reads "Not measured — <reason>" and never
 *     shows a number (it has none to show).
 */

import { useState } from 'react';
import {
  ANNOTATION_BODY_MAX,
  EVIDENCE_CLASS_LABEL,
  reasonText,
  type PracticeReview,
  type ReviewAnnotation,
  type ReviewMetric,
  type ReviewSegment,
} from './reviewTypes';

export interface PracticeReviewViewProps {
  review: PracticeReview;
  onAddNote?: (segmentIndex: number, body: string) => Promise<void> | void;
  onEditNote?: (id: string, body: string) => Promise<void> | void;
  onDeleteNote?: (id: string) => Promise<void> | void;
  /** Disables note controls while a save is in flight. */
  saving?: boolean;
  error?: string | null;
}

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatValue(m: Extract<ReviewMetric, { value: number }>): string {
  switch (m.unit) {
    case 'score':
      return `${Math.round(m.value)}`;
    case 'seconds':
      return formatClock(m.value * 1000);
    case 'wpm':
      return `${Math.round(m.value)} wpm`;
    default:
      return `${m.value}`;
  }
}

function ClassBadge({ evidenceClass }: { evidenceClass: keyof typeof EVIDENCE_CLASS_LABEL }) {
  const tone =
    evidenceClass === 'UNAVAILABLE'
      ? 'bg-gray-100 text-gray-600'
      : evidenceClass === 'MEASURED'
        ? 'bg-emerald-50 text-emerald-700'
        : evidenceClass === 'DERIVED'
          ? 'bg-blue-50 text-blue-700'
          : 'bg-amber-50 text-amber-700';
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`} data-evidence-class={evidenceClass}>
      {EVIDENCE_CLASS_LABEL[evidenceClass]}
    </span>
  );
}

function RecordingPanel({ recording }: { recording: PracticeReview['recording'] }) {
  if (!recording.available) {
    return (
      <div className="rounded-lg border border-dashed p-4" data-testid="review-recording-unavailable">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900">Recording not kept</p>
          <ClassBadge evidenceClass="UNAVAILABLE" />
        </div>
        <p className="mt-1 text-sm text-gray-500">Playback is not available: {reasonText(recording.reason)}.</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border p-4" data-testid="review-recording">
      <div className="mb-2 flex items-center gap-2">
        <p className="font-medium text-gray-900">Recording</p>
        <ClassBadge evidenceClass={recording.evidenceClass} />
      </div>
      <audio controls preload="none" src={recording.value.playbackUrl} className="w-full" />
    </div>
  );
}

function MetricTile({ metric }: { metric: ReviewMetric }) {
  return (
    <div className="rounded-lg border p-3" data-testid={`review-metric-${metric.key}`} data-metric-class={metric.evidenceClass}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-600">{metric.label}</p>
        <ClassBadge evidenceClass={metric.evidenceClass} />
      </div>
      {metric.evidenceClass === 'UNAVAILABLE' ? (
        <p className="mt-1 text-sm text-gray-500" data-testid={`review-metric-${metric.key}-unavailable`}>
          Not measured — {reasonText(metric.reason)}
        </p>
      ) : (
        <p className="mt-1 text-xl font-semibold text-gray-900">
          {formatValue(metric)}
          {metric.evidenceClass === 'MEASURED' && metric.serverVerified === false && (
            <span className="ml-2 text-xs font-normal text-gray-500">not verified by the server</span>
          )}
        </p>
      )}
    </div>
  );
}

function NoteItem({
  note,
  canEdit,
  saving,
  onEdit,
  onDelete,
}: {
  note: ReviewAnnotation;
  canEdit: boolean;
  saving?: boolean;
  onEdit?: (id: string, body: string) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  if (editing && canEdit) {
    return (
      <li className="rounded bg-yellow-50 p-2" data-annotation-id={note.id}>
        <textarea
          className="w-full rounded border p-1 text-sm"
          maxLength={ANNOTATION_BODY_MAX}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="mt-1 flex gap-2 text-xs">
          <button
            type="button"
            disabled={saving || !draft.trim()}
            onClick={async () => {
              await onEdit?.(note.id, draft.trim());
              setEditing(false);
            }}
            className="text-primary-700 disabled:opacity-50"
          >
            Save
          </button>
          <button type="button" onClick={() => setEditing(false)} className="text-gray-500">
            Cancel
          </button>
        </div>
      </li>
    );
  }
  return (
    <li className="rounded bg-yellow-50 p-2 text-sm text-gray-800" data-annotation-id={note.id}>
      <p className="whitespace-pre-wrap">{note.body}</p>
      {canEdit && (
        <div className="mt-1 flex gap-3 text-xs">
          <button type="button" disabled={saving} onClick={() => setEditing(true)} className="text-gray-600">
            Edit
          </button>
          <button type="button" disabled={saving} onClick={() => onDelete?.(note.id)} className="text-red-600">
            Delete
          </button>
        </div>
      )}
    </li>
  );
}

function SegmentRow({
  segment,
  notes,
  canAnnotate,
  saving,
  onAddNote,
  onEditNote,
  onDeleteNote,
}: {
  segment: ReviewSegment;
  notes: ReviewAnnotation[];
  canAnnotate: boolean;
  saving?: boolean;
  onAddNote?: PracticeReviewViewProps['onAddNote'];
  onEditNote?: PracticeReviewViewProps['onEditNote'];
  onDeleteNote?: PracticeReviewViewProps['onDeleteNote'];
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  return (
    <li className="py-3" data-segment-index={segment.index}>
      <div className="flex gap-3">
        {segment.startMs !== null && segment.endMs !== null && (
          <span className="w-24 shrink-0 font-mono text-xs text-gray-500" data-testid="review-segment-time">
            {formatClock(segment.startMs)}–{formatClock(segment.endMs)}
          </span>
        )}
        <p className="flex-1 text-gray-900">{segment.text}</p>
        {canAnnotate && !open && (
          <button type="button" onClick={() => setOpen(true)} className="shrink-0 text-xs text-primary-700" data-testid="review-add-note">
            Add note
          </button>
        )}
      </div>
      {notes.length > 0 && (
        <ul className="mt-2 space-y-1 pl-4" aria-label="Notes">
          {notes.map((n) => (
            <NoteItem key={n.id} note={n} canEdit={canAnnotate && n.authoredByViewer} saving={saving} onEdit={onEditNote} onDelete={onDeleteNote} />
          ))}
        </ul>
      )}
      {canAnnotate && open && (
        <div className="mt-2 pl-4">
          <textarea
            className="w-full rounded border p-2 text-sm"
            placeholder="Your note on this part"
            maxLength={ANNOTATION_BODY_MAX}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="mt-1 flex gap-2 text-xs">
            <button
              type="button"
              disabled={saving || !draft.trim()}
              onClick={async () => {
                await onAddNote?.(segment.index, draft.trim());
                setDraft('');
                setOpen(false);
              }}
              className="rounded bg-primary-600 px-2 py-1 text-white disabled:opacity-50"
            >
              Save note
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-gray-500">
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export function PracticeReviewView({ review, onAddNote, onEditNote, onDeleteNote, saving, error }: PracticeReviewViewProps) {
  const { timeline, annotations, viewer } = review;
  const canAnnotate = viewer.role === 'owner' && viewer.canAnnotate;
  const attached = annotations.items.filter((a) => a.attached);
  const detached = annotations.items.filter((a) => !a.attached);
  const notesFor = (i: number) => attached.filter((a) => a.segmentIndex === i);

  return (
    <div className="space-y-6" data-testid="practice-review">
      {viewer.role === 'coach' && (
        <p className="rounded bg-indigo-50 p-2 text-sm text-indigo-800" data-testid="review-read-only">
          Read-only: shared with you by your client.
        </p>
      )}
      {error && <p className="rounded bg-red-50 p-2 text-sm text-red-700">{error}</p>}

      <section>
        <h2 className="mb-2 text-lg font-semibold text-gray-900">Playback</h2>
        <RecordingPanel recording={review.recording} />
      </section>

      <section>
        <h2 className="mb-2 text-lg font-semibold text-gray-900">Delivery</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {review.metrics.map((m) => (
            <MetricTile key={m.key} metric={m} />
          ))}
        </div>
      </section>

      <section>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Transcript</h2>
          <ClassBadge evidenceClass={review.transcript.evidenceClass} />
        </div>
        {review.transcript.available && !review.transcript.provenance.serverVerified && (
          <p className="mb-2 text-xs text-gray-500">This transcript was submitted by the practice app and was not re-checked by the server.</p>
        )}
        {timeline.timing === 'UNAVAILABLE' && timeline.segments.length > 0 && (
          <p className="mb-2 text-sm text-gray-500" data-testid="review-timing-unavailable">
            Times not shown — {reasonText(timeline.timingReason ?? 'not_measured')}.
          </p>
        )}
        {!review.transcript.available ? (
          <p className="text-sm text-gray-500" data-testid="review-transcript-unavailable">
            No transcript — {reasonText(review.transcript.reason)}.
          </p>
        ) : (
          <ol className="divide-y rounded-lg border px-4" data-testid="review-timeline">
            {timeline.segments.map((s) => (
              <SegmentRow
                key={s.index}
                segment={s}
                notes={notesFor(s.index)}
                canAnnotate={canAnnotate}
                saving={saving}
                onAddNote={onAddNote}
                onEditNote={onEditNote}
                onDeleteNote={onDeleteNote}
              />
            ))}
          </ol>
        )}
        {!annotations.enabled && viewer.role === 'owner' && (
          <p className="mt-2 text-xs text-gray-500" data-testid="review-annotations-disabled">
            Notes are not available here yet.
          </p>
        )}
      </section>

      {detached.length > 0 && (
        <section data-testid="review-detached-notes">
          <h2 className="mb-1 text-lg font-semibold text-gray-900">Earlier notes</h2>
          <p className="mb-2 text-xs text-gray-500">The transcript was divided differently when these were written.</p>
          <ul className="space-y-1">
            {detached.map((n) => (
              <NoteItem key={n.id} note={n} canEdit={canAnnotate && n.authoredByViewer} saving={saving} onEdit={onEditNote} onDelete={onDeleteNote} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
